/**
 * App icon set: a fixed vocabulary of names mapped to lucide-react icons.
 * Use <Icon name="…"/> rather than importing lucide directly, so the set
 * stays consistent and tree-shaken (only icons listed here are bundled).
 * To add an icon: import it from lucide-react and add a name below.
 */
import type { SVGProps } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Building2,
  Calendar,
  CalendarClock,
  Camera,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  ClipboardCheck,
  Clock,
  Cloud,
  CloudCheck,
  CloudOff,
  Columns3,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  FileText,
  Flag,
  Globe,
  House,
  Image,
  Info,
  KeyRound,
  Languages,
  Layers,
  LayoutDashboard,
  ListChecks,
  ListFilter,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Menu,
  Minus,
  Moon,
  Navigation,
  Paperclip,
  PenLine,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Send,
  Settings,
  ScanLine,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Sun,
  Trash2,
  TriangleAlert,
  Upload,
  User,
  UserCheck,
  Users,
  WifiOff,
  X,
  Check,
  type LucideIcon,
} from 'lucide-react';

const ICONS = {
  home: House,
  mapPin: MapPin,
  clipboard: ClipboardCheck,
  refresh: RefreshCw,
  cloud: Cloud,
  cloudOff: CloudOff,
  cloudCheck: CloudCheck,
  user: User,
  users: Users,
  search: Search,
  chevronRight: ChevronRight,
  chevronLeft: ChevronLeft,
  chevronDown: ChevronDown,
  chevronUp: ChevronUp,
  check: Check,
  x: X,
  plus: Plus,
  minus: Minus,
  trash: Trash2,
  camera: Camera,
  image: Image,
  paperclip: Paperclip,
  help: CircleHelp,
  info: Info,
  alert: TriangleAlert,
  alertCircle: CircleAlert,
  checkCircle: CircleCheck,
  lock: Lock,
  logOut: LogOut,
  globe: Globe,
  sun: Sun,
  moon: Moon,
  settings: Settings,
  shield: ShieldCheck,
  scan: ScanLine,
  dashboard: LayoutDashboard,
  listChecks: ListChecks,
  clock: Clock,
  building: Building2,
  fileText: FileText,
  upload: Upload,
  download: Download,
  filter: ListFilter,
  columns: Columns3,
  arrowUp: ArrowUp,
  arrowDown: ArrowDown,
  menu: Menu,
  navigation: Navigation,
  pen: PenLine,
  star: Star,
  eye: Eye,
  languages: Languages,
  wifiOff: WifiOff,
  send: Send,
  key: KeyRound,
  flag: Flag,
  sliders: SlidersHorizontal,
  layers: Layers,
  external: ExternalLink,
  more: Ellipsis,
  calendar: Calendar,
  calendarClock: CalendarClock,
  mail: Mail,
  phone: Phone,
  userCheck: UserCheck,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICONS;

export function Icon({ name, size = 20, strokeWidth = 1.75, ...rest }: { name: IconName; size?: number; strokeWidth?: number } & SVGProps<SVGSVGElement>) {
  const C = ICONS[name];
  return <C size={size} strokeWidth={strokeWidth} aria-hidden="true" focusable="false" {...rest} />;
}

/**
 * Architectural corner-bracket monogram mark (docs/brand geometry) used when
 * no custom logo/icon image asset is configured.
 */
export function BrandMark({ letter, size = 'sm' }: { letter: string; size?: 'sm' | 'lg' }) {
  const dim = size === 'lg' ? 'size-14 rounded-xl text-base' : 'size-8 rounded-lg text-xs';
  const core = size === 'lg' ? 'size-7 rounded-[5px]' : 'size-4 rounded-[3px]';
  return (
    <div className={`relative flex shrink-0 items-center justify-center bg-[#181816] font-display font-bold text-white shadow-xs ring-1 ring-inset ring-white/12 ${dim}`}>
      <svg viewBox="0 0 32 32" className="pointer-events-none absolute inset-1 text-white/75" aria-hidden="true">
        <g fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square">
          <path d="M3 10 V3 H10" />
          <path d="M22 3 H29 V10" />
          <path d="M29 22 V29 H22" />
          <path d="M10 29 H3 V22" />
        </g>
      </svg>
      <span className={`relative flex items-center justify-center bg-brand text-brand-contrast shadow-2xs ${core}`}>
        {letter}
      </span>
    </div>
  );
}
